/**
 * Skill definitions for Data Agent wizard.
 * Maps product-level skill IDs to prompt registry names.
 */

import type { LucideIcon } from 'lucide-react'
import { Database, BarChart3, Slack, AlertTriangle, Brain, Activity } from 'lucide-react'
import { GoogleAdsIcon } from '../../components/icons/GoogleAdsIcon'

export interface Skill {
  id: string
  name: string
  description: string
  promptRegistryId: string  // Maps to prompts/registry.yaml
  required: boolean
  comingSoon: boolean
  adminOnly?: boolean  // Only visible to admins
  beta?: boolean
  icon: LucideIcon | React.ComponentType<any>
}

export const SKILL_CATALOG: Record<string, Skill> = {
  bigquery: {
    id: 'bigquery',
    name: 'BigQuery Analytics',
    description: 'Query BigQuery tables and analyze data',
    promptRegistryId: 'bq_specialist',
    required: true,
    comingSoon: false,
    icon: Database
  },
  charts: {
    id: 'charts',
    name: 'Chart Visualization',
    description: 'Generate interactive charts from query results (line, bar, scatter)',
    promptRegistryId: 'chart_specialist',
    required: false,
    comingSoon: false,
    icon: BarChart3
  },
  slack: {
    id: 'slack',
    name: 'Slack Digests',
    description: 'Scheduled daily digests and conversational replies',
    promptRegistryId: 'slack_specialist',
    required: false,
    comingSoon: false,
    icon: Slack
  },
  google_ads: {
    id: 'google_ads',
    name: 'Google Ads Management',
    description: 'ML-powered campaign optimization: pause underperformers, adjust budgets, manage keywords',
    promptRegistryId: 'google_ads_specialist',
    required: false,
    comingSoon: false,
    adminOnly: true,
    beta: true,
    icon: GoogleAdsIcon
  },
  ltv: {
    id: 'ltv',
    name: 'Customer LTV Prediction',
    description: 'ML-powered customer lifetime value forecasting and segmentation',
    promptRegistryId: 'ltv_specialist',
    required: false,
    comingSoon: true,
    adminOnly: true,
    icon: Brain
  },
  demand_pulse: {
    id: 'demand_pulse',
    name: 'Demand Pulse',
    description: 'Google search demand for this market: keyword volumes, categories, mindsets and forecasts',
    promptRegistryId: 'demand_pulse_specialist',
    required: false,
    comingSoon: false,
    icon: Activity,
  },
  anomaly: {
    id: 'anomaly',
    name: 'Anomaly Detection',
    description: 'Automatically detect unusual patterns and outliers',
    promptRegistryId: 'anomaly_detector',
    required: false,
    comingSoon: true,
    icon: AlertTriangle
  }
}

/**
 * Get available skills filtered by user role.
 */
export function getAvailableSkills(isAdmin: boolean = false): Skill[] {
  return Object.values(SKILL_CATALOG).filter(skill => {
    if (skill.adminOnly && !isAdmin) {
      return false
    }
    return true
  })
}

export const AVAILABLE_SKILLS = Object.values(SKILL_CATALOG)

export const REQUIRED_SKILLS = AVAILABLE_SKILLS.filter(s => s.required).map(s => s.id)

/**
 * Get default skills for a new agent.
 */
export function getDefaultSkills(): string[] {
  return ['bigquery']
}

/**
 * Infer skills from legacy agent configuration (for backward compat).
 */
export function inferSkillsFromLegacy(agent: {
  digest_enabled?: boolean
  schedule?: string
  slack_channel?: string
  slack_channel_id?: string
}): string[] {
  const skills = ['bigquery']  // Always present
  
  // Add slack if digest or Slack is configured
  const hasDigest = agent.digest_enabled !== false && !!(agent.schedule && agent.schedule.trim())
  const hasSlack = !!(agent.slack_channel || agent.slack_channel_id)
  
  if (hasDigest || hasSlack) {
    skills.push('slack')
  }
  
  return skills
}
